import mapboxgl from "mapbox-gl"
import { stateToRegionByName } from "../../../../../constants/Map"
import { fNumber } from "../../../../../utils/formatNumber"
import {
  ICountyPublicWaterSystemResult,
  MapWellWaterSystemMetrics } from "../../../../../types"
import { Dispatch } from "redux"

let currentPopup: mapboxgl.Popup | null = null;

interface PopupConfig {
  title: string;
  subtitle: string;
  region?: string;
  metrics?: {
    totalHousingUnits?: number;
  };
  lngLat: mapboxgl.LngLat;
  map: mapboxgl.Map;
}

const createPopup = (config: PopupConfig): mapboxgl.Popup => {
  const { title, subtitle, region, metrics, lngLat, map } = config;

  const popupContent = `
    <div class="map-popup">
      <div class="popup-header">
        <h3>${title} <span class="state-code">${subtitle}</span></h3>
      </div>
      <div class="popup-content">
        ${region ? `
          <div class="info-row">
            <span class="label">Region:</span>
            <span class="value">${region}</span>
          </div>
        ` : ''}
        ${metrics ? `
          <div class="metrics-section">
            <div class="metrics-grid">
              <div class="metric-item full-width">
                <span class="metric-value">${fNumber(metrics.totalHousingUnits ?? 0)}</span>
                <span class="metric-label">Total Housing Units</span>
              </div>
            </div>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  currentPopup?.remove();
  
  currentPopup = new mapboxgl.Popup({ 
    offset: 25,
    className: 'custom-popup',
    closeButton: false
  })
    .setLngLat(lngLat)
    .setHTML(popupContent)
    .addTo(map);

  currentPopup.once('close', () => {
    currentPopup = null;
  });

  return currentPopup;
};

export const handleHover = (
  e: mapboxgl.MapMouseEvent,
  mapRef?: React.RefObject<mapboxgl.Map>,
  //regionsHousingUnitsRef?: React.RefObject<IRegionPublicWaterSystemResult[]>,
  countyHousingUnitsRef?: React.RefObject<ICountyPublicWaterSystemResult[]>,
  //geoBlocksGroupUnitsRef?: React.RefObject<IWaterSystemGeoBlocks[]>,
  wellWaterSystemMapMetricsRef?: React.RefObject<MapWellWaterSystemMetrics[]>,
  dispatch?: Dispatch
): mapboxgl.Popup | null => {
  if (!e.features?.length || !mapRef?.current || !dispatch) {
    return null;
  }
  
  const geographicName = e.features[0].properties?.name;
  if (!geographicName) {
    return null;
  }
  const zoomLevel = mapRef.current.getZoom();
  //const isStateZoom = zoomLevel < 6
  const isCountyZoom = zoomLevel >= 6 && zoomLevel < 9.5;
  //const isGeoBlockZoom = zoomLevel >= 9.5 && zoomLevel < 18;
  
  const { epaRegion, code } = stateToRegionByName[geographicName] || {};

  // Handle county-level zoom
  if (isCountyZoom) {
    const countyData = countyHousingUnitsRef?.current?.find(
      county => county.county_name.toLowerCase() === geographicName.toLowerCase()
    );

    if (countyData) {
      return createPopup({
        title: countyData.county_name,
        subtitle: `County Code: ${countyData.county_code}`,
        metrics: {
          totalHousingUnits: countyData.totalHousingUnits
        },
        lngLat: e.lngLat,
        map: mapRef.current
      });
    }
  }


  // Handle region-level zoom (default)
  if (epaRegion && code) {
    const stateMetrics = wellWaterSystemMapMetricsRef?.current?.find(
      metrics => metrics.stateCode === code
    );

    return createPopup({
      title: geographicName,
      subtitle: `(${code})`,
      region: epaRegion,
      metrics: stateMetrics?.metrics,
      lngLat: e.lngLat,
      map: mapRef.current
    });
  }

  return null;
};

export const resetWellMapHoverState = () => {
  if (currentPopup) {
    currentPopup.remove();
    currentPopup = null;
  }
}; 